import { strToU8, unzipSync, zipSync } from "fflate";
import type { AssetVariant, PhotoFolioProject } from "../domain/project";
import { normalizeProject } from "../domain/project";
import { db } from "../storage/database";
import { readAssetFile, writeAssetFile } from "../storage/asset-store";

const variants: AssetVariant[] = ["original", "preview", "thumbnail"];

export async function createProjectArchive(project: PhotoFolioProject) {
  const files: Record<string, Uint8Array> = {
    "manifest.json": strToU8(JSON.stringify({
      format: "photofolio-project",
      schemaVersion: 1,
      createdWith: "PhotoFolio",
      createdAt: new Date().toISOString(),
    }, null, 2)),
    "project.json": strToU8(JSON.stringify(project, null, 2)),
    "licenses/open-source-notices.txt": strToU8("PhotoFolio uses Dexie, exifr, Pica, dnd-kit, fflate, React and Phosphor Icons. See the project README for license links."),
  };
  const assets = await db.assets.where("projectId").equals(project.id).toArray();
  await Promise.all(assets.flatMap((asset) => variants.map(async (variant) => {
    const blob = await readAssetFile(project.id, asset.id, variant);
    files[`assets/${asset.id}/${variant}`] = new Uint8Array(await blob.arrayBuffer());
  })));
  files["assets/metadata.json"] = strToU8(JSON.stringify(assets, null, 2));
  return new Blob([zipSync(files, { level: 6 }) as BlobPart], { type: "application/zip" });
}

export async function importProjectArchive(file: File) {
  try {
    const archive = unzipSync(new Uint8Array(await file.arrayBuffer()));
    if (!archive["manifest.json"] || !archive["project.json"] || !archive["assets/metadata.json"]) {
      throw new Error("草稿缺少必要文件");
    }
    const manifest = JSON.parse(new TextDecoder().decode(archive["manifest.json"]));
    if (manifest.format !== "photofolio-project" || manifest.schemaVersion !== 1) {
      throw new Error("草稿格式或版本不受支持");
    }
    const project = normalizeProject(
      JSON.parse(new TextDecoder().decode(archive["project.json"])),
      { preserveTimestamps: false },
    );
    const assets = JSON.parse(new TextDecoder().decode(archive["assets/metadata.json"]));
    if (!Array.isArray(assets)) throw new Error("草稿图片清单无效");
    const assetIds = new Set<string>();
    for (const asset of assets) {
      if (!asset || typeof asset !== "object" || typeof asset.id !== "string" || !asset.id || typeof asset.mimeType !== "string") {
        throw new Error("草稿图片清单无效");
      }
      if (assetIds.has(asset.id)) throw new Error("草稿图片清单包含重复资源");
      assetIds.add(asset.id);
    }
    for (const item of project.items) {
      if (!assetIds.has(item.assetId)) throw new Error(`草稿缺少照片资源: ${item.assetId}`);
      for (const variant of variants) {
        if (!archive[`assets/${item.assetId}/${variant}`]) {
          throw new Error(`草稿缺少照片文件: ${item.assetId}/${variant}`);
        }
      }
    }

    await db.assets.bulkPut(assets);
    await Promise.all(assets.flatMap((asset: { id: string; mimeType: string }) => variants.map(async (variant) => {
      const bytes = archive[`assets/${asset.id}/${variant}`];
      if (!bytes) return;
      const type = variant === "original" ? asset.mimeType : "image/webp";
      await writeAssetFile(project.id, asset.id, variant, new Blob([bytes as BlobPart], { type }));
    })));
    await db.projects.put(project);
    return project;
  } catch (error) {
    if (error instanceof Error && (error.message.startsWith("草稿") || error.message.startsWith("项目"))) {
      throw error;
    }
    throw new Error("草稿损坏、不完整或版本不受支持");
  }
}
